import { Lock } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { FeatureBadge } from '@/services/api/billing/useGetBillingSubscription'

/**
 * Indicator on locked feature entry points. "upgrade" (default): a small
 * circular lock, icon only so it can sit at the edge of a button/menu item
 * without covering the label. "coming_soon": a compact "Soon" pill — the
 * feature's plan isn't on sale yet. The tooltip carries the explanation.
 */
export default function LockBadge({
  className,
  badge = 'upgrade',
  tooltip,
}: {
  className?: string
  badge?: FeatureBadge
  /** Pass null to render the badge without a tooltip. */
  tooltip?: string | null
}) {
  const comingSoon = badge === 'coming_soon'
  const text =
    tooltip === undefined
      ? comingSoon
        ? 'Coming soon'
        : 'Feature is not available in your subscription'
      : tooltip

  const icon = comingSoon ? (
    <span
      className={cn(
        'inline-flex h-4 shrink-0 items-center whitespace-nowrap rounded-full bg-purple-default px-1.5 text-[10px] font-semibold leading-none text-white',
        className,
      )}
    >
      Soon
    </span>
  ) : (
    <span
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-purple-default text-white',
        className,
      )}
    >
      <Lock className="size-2.5" />
    </span>
  )

  if (!text) return icon

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
